import { inject, Injectable } from '@angular/core';
import { DialogService } from '../../../core/services/dialog.service';
import { UserService } from '../../user/service/user.service';
import { AddDutyDialogComponent, type AddDutyPrefill } from '../components/add-duty-dialog/add-duty-dialog.component';
import { ApplyDutyTemplateDialogComponent } from '../components/apply-duty-template-dialog/apply-duty-template-dialog.component';
import {
  RoutineBuilderDialogComponent,
  type RoutineBuilderDialogData,
} from '../components/routine-builder-dialog/routine-builder-dialog.component';
import { LinkingWizardComponent } from '../../user/components/linking-wizard/linking-wizard.component';
import { PARENT_DIALOG_PANEL_CLASS } from '../../../shared/constants/parent-dialog.constants';
import type { ParentDuty } from '../models/parent-duty.model';
import { ParentIdentityService } from '../../identity/services/parent-identity.service';
import { IdentityNavigationService } from '../../identity/services/identity-navigation.service';
import { FamilyKidsService } from '../../parent-family/services/family-kids.service';
import { FamilyHubService } from '../../parent-family/services/family-hub.service';
import { Router } from '@angular/router';
import { hasKidsMaxSeatAvailable } from '../../user/utils/plan-kids-limit.util';

@Injectable({ providedIn: 'root' })
export class HomeDialogService {
  #dialogService = inject(DialogService);
  #userService = inject(UserService);
  #identity = inject(ParentIdentityService);
  #identityNav = inject(IdentityNavigationService);
  #familyKids = inject(FamilyKidsService);
  #familyHub = inject(FamilyHubService);
  #router = inject(Router);

  openAddDutyDialog(): void {
    this.#dialogService.open(
      AddDutyDialogComponent,
      { mode: 'new' },
      {
        panelClass: [PARENT_DIALOG_PANEL_CLASS, 'add-duty-dialog'],
      },
    );
  }

  openAddDutyFromPatrol(options: {
    selectedKids: string[];
    prefill: AddDutyPrefill;
  }): void {
    this.#dialogService.open(
      AddDutyDialogComponent,
      {
        mode: 'new',
        selectedKids: options.selectedKids,
        prefill: options.prefill,
      },
      {
        panelClass: [PARENT_DIALOG_PANEL_CLASS, 'add-duty-dialog'],
      },
    );
  }

  openApplyDutyTemplateDialog(): void {
    if (!this.#userService.isQuotaCapabilityAllowed('duties.routines')) {
      void this.#router.navigate(['/profile'], {
        queryParams: { segment: 'subscriptions' },
      });
      return;
    }
    this.#dialogService.open(
      ApplyDutyTemplateDialogComponent,
      {},
      {
        panelClass: [PARENT_DIALOG_PANEL_CLASS, 'add-duty-dialog'],
      },
    );
  }

  openRoutineBuilderDialog(data: RoutineBuilderDialogData): void {
    if (!this.#userService.isQuotaCapabilityAllowed('duties.routines')) {
      void this.#router.navigate(['/profile'], {
        queryParams: { segment: 'subscriptions' },
      });
      return;
    }
    this.#dialogService.open(
      RoutineBuilderDialogComponent,
      data,
      {
        panelClass: [PARENT_DIALOG_PANEL_CLASS, 'add-duty-dialog', 'routine-builder-dialog'],
      },
    );
  }

  openEditDutyDialog(duty: ParentDuty): void {
    this.#dialogService.open(
      AddDutyDialogComponent,
      {
        mode: 'edit',
        duty,
        selectedKids: [duty.reciever],
      },
      {
        panelClass: [PARENT_DIALOG_PANEL_CLASS, 'add-duty-dialog'],
      },
    );
  }

  async openAddKidDialog(): Promise<void> {
    await this.#identity.whenConfigReady();
    if (this.#identity.layerEnabled() && !this.#identity.canExpandFamily()) {
      void this.#identityNav.navigateToVerification();
      return;
    }

    const uid = this.#userService.user()?.id ?? '';
    const role = this.#familyHub.familyQuery
      .data()
      ?.members?.find((m) => m.userId === uid)?.role;
    const canAddByRole = role === 'PRIMARY' || role === 'CO_PARENT';
    const planOk = hasKidsMaxSeatAvailable({
      quotasEnforced: this.#userService.quotasEnforced(),
      paymentsEnabled: this.#userService.paymentsEnabled(),
      scopes: this.#userService.entitlement()?.scopes,
      activeChildCount: this.#familyKids.activeChildCount(),
    });
    if (!canAddByRole || !planOk) {
      if (canAddByRole && !planOk && this.#userService.paymentsEnabled()) {
        void this.#router.navigate(['/profile'], {
          queryParams: { segment: 'subscriptions' },
        });
      }
      return;
    }

    this.#dialogService.open(
      LinkingWizardComponent,
      { user: this.#userService.user() },
      { panelClass: PARENT_DIALOG_PANEL_CLASS },
    );
  }
}
